/*
 * Portions of this component's visual styling are adapted from shadcn/ui.
 * Copyright (c) 2023 shadcn. Licensed under the MIT License.
 */

import { IconX } from "./icons/x.js";

function requestClose(host: HTMLElement): void {
  host.dispatchEvent(new CustomEvent("close", { bubbles: true, composed: true }));
}

function isVisible(element: HTMLElement): boolean {
  let current: HTMLElement | null = element;
  while (current) {
    const style = getComputedStyle(current);
    if (current.hidden || current.getAttribute("aria-hidden") === "true" || style.display === "none" || style.visibility === "hidden") return false;
    const root = current.getRootNode();
    current = current.parentElement ?? (root instanceof ShadowRoot ? root.host : null);
  }
  return true;
}

function focusableElements(root: ParentNode): HTMLElement[] {
  const selector = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const focusable: HTMLElement[] = [];
  const visit = (parent: ParentNode) => {
    for (const element of Array.from(parent.children)) {
      if (element instanceof HTMLElement && element.matches(selector) && isVisible(element)) {
        focusable.push(element);
      }
      if (element.localName === "slot") {
        for (const assigned of (element as HTMLSlotElement).assignedElements({ flatten: true })) {
          visit(assigned);
          if (assigned.shadowRoot) visit(assigned.shadowRoot);
        }
      } else {
        visit(element);
      }
      if (element.shadowRoot) visit(element.shadowRoot);
    }
  };
  visit(root);
  return focusable;
}

function activeElement(): HTMLElement | null {
  let active = document.activeElement;
  while (active instanceof HTMLElement && active.shadowRoot?.activeElement) active = active.shadowRoot.activeElement;
  return active instanceof HTMLElement ? active : null;
}

const styles = css`
  :host {
    display: contents;
  }

  .overlay {
    align-items: center;
    background: rgb(0 0 0 / 50%);
    inset: 0;
    justify-content: center;
    padding: 16px;
    position: fixed;
    z-index: 200;
  }

  .dialog {
    background: var(--background, #ffffff);
    border: 1px solid var(--border, #e4e4e7);
    border-radius: var(--radius-lg, 10px);
    box-shadow: 0 10px 38px rgb(0 0 0 / 18%);
    box-sizing: border-box;
    color: var(--foreground, #09090b);
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-height: calc(100dvh - 32px);
    max-width: 512px;
    overflow: auto;
    padding: 24px;
    position: relative;
    width: 100%;
  }

  :host([size="wide"]) .dialog {
    max-width: min(980px, calc(100dvw - 32px));
  }

  :host([size="settings"]) .dialog {
    gap: 0;
    height: min(680px, calc(100dvh - 32px));
    max-height: calc(100dvh - 32px);
    max-width: min(1000px, calc(100dvw - 32px));
    overflow: hidden;
    padding: 0;
  }
  :host([size="settings"]) .header {
    align-items: center;
    border-bottom: 1px solid var(--border);
    min-height: 48px;
    padding: 0 52px 0 20px;
  }
  :host([size="settings"]) .title { font-size: 1.125rem; line-height: 1; }
  :host([size="settings"]) .close { top: 12px; }
  :host([size="settings"]) .content { flex: 1; min-height: 0; overflow: hidden; }

  :host([size="fullscreen"]) .overlay { padding: 0; }
  :host([size="fullscreen"]) .dialog {
    border: 0;
    border-radius: 0;
    gap: 0;
    height: 100dvh;
    max-height: none;
    max-width: none;
    padding: 0;
  }
  :host([size="fullscreen"]) .header { border-bottom: 1px solid var(--border); min-height: 48px; padding: 0 48px 0 16px; }
  :host([size="fullscreen"]) .content { flex: 1; min-height: 0; overflow: auto; }

  .header {
    display: grid;
    gap: 6px;
    padding-right: 28px;
  }

  .title {
    font-size: 1.05rem;
    font-weight: 600;
    line-height: 1.3;
  }

  .title:empty {
    display: none;
  }

  .description {
    color: var(--muted-foreground, #71717a);
    font-size: 0.875rem;
    line-height: 1.45;
  }

  .description:empty {
    display: none;
  }

  .close {
    align-items: center;
    background: var(--background, #ffffff);
    border: 0;
    border-radius: 6px;
    color: var(--muted-foreground, #71717a);
    cursor: pointer;
    display: inline-flex;
    height: 24px;
    justify-content: center;
    padding: 0;
    position: absolute;
    right: 16px;
    top: 16px;
    width: 24px;
    z-index: 2;
  }

  .close:hover {
    background: var(--muted, #f4f4f5);
    color: var(--foreground, #18181b);
  }

  .close:focus-visible {
    box-shadow: 0 0 0 3px var(--ring-shadow, rgb(24 24 27 / 12%));
    outline: none;
  }

  .close .icon {
    align-items: center;
    display: inline-flex;
    height: 16px;
    justify-content: center;
    width: 16px;
  }

  .close .icon > * {
    height: 16px;
    width: 16px;
  }

  .footer {
    align-items: center;
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }

  .footer:not(:has(::slotted(*))) {
    display: none;
  }

  @media (max-width: 767px) {
    :host([size="settings"]) .overlay { padding: 0; }
    :host([size="settings"]) .dialog {
      border: 0;
      border-radius: 0;
      height: 100dvh;
      max-height: none;
      max-width: none;
    }
    :host([size="settings"]) .header { padding-left: 16px; }
  }
`;

export class AppDialog extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppDialog)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["open","data-open","title","data-title","description","data-description","size","data-size","dialogid","data-dialog-id"];

  #open = false;
  #title = "";
  #description = "";
  #size = "";
  #dialogId = "";
  #openSet = false;
  #titleSet = false;
  #descriptionSet = false;
  #sizeSet = false;
  #dialogIdSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #attr0;
  #attrLast0;
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get open() {
    return this.#open;
  }

  set open(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#open, next)) return;
    this.#open = next;
    this.#openSet = true;
    this.#updateBindings('open');
  }

  get title() {
    return this.#title;
  }

  set title(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#title, next)) return;
    this.#title = next;
    this.#titleSet = true;
    this.#updateBindings('title');
  }

  get description() {
    return this.#description;
  }

  set description(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#description, next)) return;
    this.#description = next;
    this.#descriptionSet = true;
    this.#updateBindings('description');
  }

  get size() {
    return this.#size;
  }

  set size(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#size, next)) return;
    this.#size = next;
    this.#sizeSet = true;
    this.#updateBindings('size');
  }

  get dialogId() {
    return this.#dialogId;
  }

  set dialogId(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#dialogId, next)) return;
    this.#dialogId = next;
    this.#dialogIdSet = true;
    this.#updateBindings('dialogId');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-open')) this.open = this.dataset.open;
    if (this.hasAttribute('data-title')) this.title = this.dataset.title;
    if (this.hasAttribute('data-description')) this.description = this.dataset.description;
    if (this.hasAttribute('data-size')) this.size = this.dataset.size;
    if (this.hasAttribute('data-dialog-id')) this.dialogId = this.dataset.dialogId;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.open = props.open;
      this.title = props.title;
      this.description = props.description;
      this.size = props.size;
      this.dialogId = props.dialogId;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#openSet) this.open = this.dataset.open;
    if (!this.#titleSet) this.title = this.dataset.title;
    if (!this.#descriptionSet) this.description = this.dataset.description;
    if (!this.#sizeSet) this.size = this.dataset.size;
    if (!this.#dialogIdSet) this.dialogId = this.dataset.dialogId;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => {
    let previouslyFocused: HTMLElement | null = null;
    let previousOverflow = "";
    let locked = false;
    const overlay = this.shadowRoot?.querySelector<HTMLElement>(".overlay");
    const syncOpenState = () => {
        const visible = overlay?.style.display !== "none";
        if (visible && !locked) {
            locked = true;
            previouslyFocused = activeElement();
            previousOverflow = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            queueMicrotask(() => this.shadowRoot?.querySelector<HTMLElement>(".close")?.focus());
        }
        else if (!visible && locked) {
            locked = false;
            document.body.style.overflow = previousOverflow;
            previouslyFocused?.focus();
            previouslyFocused = null;
        }
    };
    const observer = new MutationObserver(syncOpenState);
    if (overlay)
        observer.observe(overlay, { attributes: true, attributeFilter: ["style"] });
    syncOpenState();
    const onKey = (event: KeyboardEvent) => {
        if (!this.#open)
            return;
        if (event.key === "Escape") {
            event.preventDefault();
            requestClose(this);
            return;
        }
        if (event.key !== "Tab")
            return;
        const dialog = this.shadowRoot?.querySelector<HTMLElement>(".dialog");
        if (!dialog)
            return;
        const focusable = focusableElements(dialog);
        if (focusable.length === 0)
            return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = activeElement();
        if (event.shiftKey && active === first) {
            event.preventDefault();
            last.focus();
        }
        else if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
        }
    };
    document.addEventListener("keydown", onKey);
    return () => {
        observer.disconnect();
        document.removeEventListener("keydown", onKey);
        if (locked)
            document.body.style.overflow = previousOverflow;
    };
})();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
    this.#effect0();
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
    if (this.#effect0Cleanup) { this.#effect0Cleanup(); this.#effect0Cleanup = undefined; }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'open':
      case 'data-open':
        this.open = newValue;
        break;
      case 'title':
      case 'data-title':
        this.title = newValue;
        break;
      case 'description':
      case 'data-description':
        this.description = newValue;
        break;
      case 'size':
      case 'data-size':
        this.size = newValue;
        break;
      case 'dialogid':
      case 'data-dialog-id':
        this.dialogId = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    
    return `<style>${this.#escText(styles)}</style><div class="overlay" style="${this.#escAttr(this.#open ? "display:flex" : "display:none")}" data-argon="8036" data-argon-bind="0"><div class="dialog" role="dialog" aria-modal="true" aria-labelledby="dialog-title" aria-describedby="dialog-description" part="dialog"><button class="close" type="button" aria-label="Close" data-argon="8365"><span class="icon"><icon-x><!--argon-text:0-->${""}<!--/argon-text:0--></icon-x></span></button><div class="header"><div class="title" id="dialog-title"><!--argon-text:1-->${this.#escText(this.#title)}<!--/argon-text:1--></div><div class="description" id="dialog-description"><!--argon-text:2-->${this.#escText(this.#description)}<!--/argon-text:2--></div></div><div class="content"><slot></slot></div><div class="footer"><slot name="footer"></slot></div></div></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  #effect0Cleanup;
  #effect0() {
    if (this.#effect0Cleanup) this.#effect0Cleanup();
    const cleanup = (() => {
    if (this.#size) {
        this.setAttribute("size", this.#size);
    }
    else {
        this.removeAttribute("size");
    }
    if (this.#dialogId) {
        this.dataset.dialog = this.#dialogId;
    }
    else {
        delete this.dataset.dialog;
    }
})();
    this.#effect0Cleanup = typeof cleanup === 'function' ? cleanup : undefined;
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'title') {
      const __argonNext = `${this.#escText(this.#title)}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined || prop === 'description') {
      const __argonNext = `${this.#escText(this.#description)}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'open') && this.#attr0) {
      const __argonNext = `${this.#open ? "display:flex" : "display:none"}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('style', __argonNext);
      }
    }
    if (this.#hooksLive && (prop === 'size' || prop === 'dialogId')) this.#effect0();
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '8036').forEach(el => this.#listen(el, '8036', 'click', (event: Event) => {
    if (event.target === event.currentTarget)
        requestClose(this);
}));
    this.#eventTargets(scope, '8365').forEach(el => this.#listen(el, '8365', 'click', () => requestClose(this)));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
